import { useEffect, useMemo, useState } from 'react'
import { fetchBacktestEvents } from '../../bot/adapters/eventFetcher'
import type { OrderData } from '../../bot/types/chartData'
import { entryTimeOf, ticketsByEntryTime } from '../../bot/utils/orderPairing'

export interface UseWalkthroughOrdersOptions {
  btid: string
  /** The run's REAL range, unix seconds — what the events are fetched over. */
  rangeStartTs: number
  rangeEndTs: number
  /** The DISPLAY span, unix seconds — what the chart loads and can be scrolled to. */
  displayStartTs: number
  displayEndTs: number
}

export interface WalkthroughOrders {
  /**
   * Every order event of the run whose position is on the replay chart, entry and exit alike.
   *
   * Not "every event of the run": see the filter below.
   */
  orders: OrderData[]
  /** entry time -> ticket number, for the set above. */
  tickets: ReadonlyMap<number, number>
}

/**
 * The walkthrough's order events, fetched once for the whole run and numbered.
 *
 * Lives above both readers on purpose. The trades pane and the chart's order tooltip have to
 * show the same trades under the same numbers, and neither can work that out alone: the chart
 * only ever holds the orders for the candle window it has paged in, and the pane only ever
 * holds what the playhead has revealed. Fetching and numbering once, here, is what makes the
 * two consistent by construction rather than by two implementations agreeing.
 *
 * One request for the run rather than a per-frame cost, and independent of the chart's own
 * windowed orders — a trade list that lost rows when the user panned would be actively
 * misleading during validation.
 */
export default function useWalkthroughOrders({
  btid,
  rangeStartTs,
  rangeEndTs,
  displayStartTs,
  displayEndTs,
}: UseWalkthroughOrdersOptions): WalkthroughOrders {
  const [all, setAll] = useState<OrderData[]>([])

  // The REAL range is fetched, not the display span: the server pads the period with indicator
  // warm-up and rounds it to whole UTC days, and asking for a narrower window would leave the
  // boundary cases to the endpoint's own inclusivity rules. What is in view is decided below,
  // where it can be decided exactly.
  useEffect(() => {
    let cancelled = false
    fetchBacktestEvents(btid, rangeStartTs, rangeEndTs)
      .then((rows) => {
        if (!cancelled) setAll(rows)
      })
      .catch(() => {
        /* fetchBacktestEvents already swallows and returns [] */
      })
    return () => {
      cancelled = true
    }
  }, [btid, rangeStartTs, rangeEndTs])

  return useMemo(() => {
    // EVERYTHING LISTED HAS TO BE FINDABLE ON THE CHART. The run's range is much wider than
    // what the chart shows — the code trades through the warm-up bars before the drawings and
    // through whatever is left of the last UTC day after them — and a row for a position whose
    // entry bar was never loaded is a row that cannot be clicked, cannot be checked against a
    // marker, and quietly takes a ticket number off the trades that can.
    //
    // Judged on the ENTRY, so a position taken in view keeps its row even if the replay can
    // never reveal its close. It reads as still open, which within this span it is.
    const orders = all.filter((o) => {
      const entry = entryTimeOf(o)
      return entry !== null && entry >= displayStartTs && entry <= displayEndTs
    })
    return { orders, tickets: ticketsByEntryTime(orders) }
  }, [all, displayStartTs, displayEndTs])
}
